import { useState, useEffect, useRef, useCallback } from "react";
import {
  LIVEBRIDGE_ROOM_ID,
  fetchLiveBridgeMessages,
  updateLiveBridgePreview
} from "./liveBridgeApi";

const LIVEBRIDGE_VIEWER_SESSION_KEY = "LIVEBRIDGE_VIEWER_PASSWORD";
const LIVEBRIDGE_VIEWER_POLL_MS = 850;

export function useViewerSession({ page, t }) {
  const [liveBridgeViewerPasswordInput, setLiveBridgeViewerPasswordInput] = useState("");
  const [liveBridgeViewerPassword, setLiveBridgeViewerPassword] = useState(() => {
    if (typeof window === "undefined") {
      return "";
    }
    return String(window.sessionStorage.getItem(LIVEBRIDGE_VIEWER_SESSION_KEY) || "").trim();
  });
  const [liveBridgeMessages, setLiveBridgeMessages] = useState([]);
  const [liveBridgePreview, setLiveBridgePreview] = useState(null);
  const [liveBridgeViewerLoading, setLiveBridgeViewerLoading] = useState(false);
  const [liveBridgeViewerError, setLiveBridgeViewerError] = useState("");
  const [liveBridgeLastUpdated, setLiveBridgeLastUpdated] = useState("");
  const [liveBridgeConnectionStatus, setLiveBridgeConnectionStatus] = useState("connected");

  const liveBridgeViewerFeedRef = useRef(null);

  // Persist viewer password to sessionStorage.
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (liveBridgeViewerPassword) {
      window.sessionStorage.setItem(LIVEBRIDGE_VIEWER_SESSION_KEY, liveBridgeViewerPassword);
    } else {
      window.sessionStorage.removeItem(LIVEBRIDGE_VIEWER_SESSION_KEY);
    }
  }, [liveBridgeViewerPassword]);

  const applyLiveBridgeRoomPayload = useCallback((payload) => {
    setLiveBridgeMessages(Array.isArray(payload?.messages) ? payload.messages : []);
    setLiveBridgePreview(payload?.preview || null);
    setLiveBridgeLastUpdated(payload?.updatedAt || "");
  }, []);

  const appendLiveBridgeLocalMessage = useCallback((message, session) => {
    if (!message) {
      return;
    }

    setLiveBridgeLastUpdated(message.createdAt || new Date().toISOString());
    setLiveBridgeMessages((prev) => {
      const currentSessionId = session?.id || null;
      const filtered =
        currentSessionId && prev.some((e) => e.sessionId !== currentSessionId)
          ? prev.filter((e) => e.sessionId === currentSessionId)
          : prev;
      return [...filtered, message].slice(-60);
    });
  }, []);

  const loadLiveBridgeMessages = useCallback(
    async (password) => {
      const payload = await fetchLiveBridgeMessages({
        password,
        roomId: LIVEBRIDGE_ROOM_ID
      });
      applyLiveBridgeRoomPayload(payload);
      setLiveBridgeViewerError("");
      setLiveBridgeConnectionStatus("connected");
      return payload;
    },
    [applyLiveBridgeRoomPayload]
  );

  const pushLiveBridgePreview = useCallback(async ({ englishText, chineseText, sender }) => {
    const payload = await updateLiveBridgePreview({
      sender,
      englishText,
      chineseText,
      roomId: LIVEBRIDGE_ROOM_ID
    });
    setLiveBridgePreview(payload?.preview || null);
    setLiveBridgeLastUpdated(payload?.updatedAt || new Date().toISOString());
    return payload;
  }, []);

  // Viewer polling — active only when on the viewer page with a valid password.
  useEffect(() => {
    if (page !== "liveBridgeViewer" || !liveBridgeViewerPassword) {
      return undefined;
    }

    let active = true;

    const refreshViewer = async () => {
      setLiveBridgeViewerLoading(true);
      setLiveBridgeConnectionStatus("syncing");

      try {
        await loadLiveBridgeMessages(liveBridgeViewerPassword);

        if (!active) {
          return;
        }

        setLiveBridgeViewerError("");
      } catch (error) {
        if (!active) {
          return;
        }

        setLiveBridgeConnectionStatus("disconnected");
        setLiveBridgeViewerError(
          error instanceof Error && error.message
            ? error.message
            : "LiveBridge viewer refresh failed."
        );
      } finally {
        if (active) {
          setLiveBridgeViewerLoading(false);
        }
      }
    };

    refreshViewer();
    const intervalId = window.setInterval(refreshViewer, LIVEBRIDGE_VIEWER_POLL_MS);

    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [loadLiveBridgeMessages, liveBridgeViewerPassword, page]);

  // Scroll viewer feed to bottom when new messages arrive.
  useEffect(() => {
    if (
      page !== "liveBridgeViewer" ||
      !liveBridgeViewerPassword ||
      !liveBridgeViewerFeedRef.current
    ) {
      return;
    }

    liveBridgeViewerFeedRef.current.scrollTop = liveBridgeViewerFeedRef.current.scrollHeight;
  }, [page, liveBridgePreview?.updatedAt, liveBridgeViewerPassword, liveBridgeMessages.length]);

  const handleLiveBridgeUnlock = useCallback(async () => {
    const password = String(liveBridgeViewerPasswordInput || "").trim();

    if (!password) {
      setLiveBridgeViewerError(t("livebridge_password_label"));
      return;
    }

    setLiveBridgeViewerLoading(true);
    setLiveBridgeViewerError("");

    try {
      await loadLiveBridgeMessages(password);
      setLiveBridgeViewerPassword(password);
    } catch (error) {
      setLiveBridgeViewerPassword("");
      setLiveBridgeConnectionStatus("disconnected");
      setLiveBridgeViewerError(
        error instanceof Error && error.message ? error.message : "Viewer password required."
      );
    } finally {
      setLiveBridgeViewerLoading(false);
    }
  }, [liveBridgeViewerPasswordInput, loadLiveBridgeMessages, t]);

  const handleLiveBridgeLock = useCallback(() => {
    setLiveBridgeViewerPassword("");
    setLiveBridgeViewerPasswordInput("");
    setLiveBridgeMessages([]);
    setLiveBridgeViewerError("");
    setLiveBridgeConnectionStatus("connected");
  }, []);

  const liveBridgeConnectionLabel =
    liveBridgeConnectionStatus === "connected"
      ? t("livebridge_status_connected")
      : liveBridgeConnectionStatus === "syncing"
      ? t("livebridge_status_syncing")
      : t("livebridge_status_disconnected");

  const liveBridgeUpdatedLabel = liveBridgeLastUpdated
    ? new Date(liveBridgeLastUpdated).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit"
      })
    : "";

  return {
    liveBridgeViewerPasswordInput,
    liveBridgeViewerPassword,
    liveBridgeMessages,
    liveBridgePreview,
    liveBridgeViewerLoading,
    liveBridgeViewerError,
    liveBridgeLastUpdated,
    liveBridgeConnectionStatus,
    liveBridgeConnectionLabel,
    liveBridgeUpdatedLabel,
    liveBridgeViewerFeedRef,
    // Internal helpers needed by useNetworkSync
    setLiveBridgeConnectionStatus,
    setLiveBridgePreview,
    applyLiveBridgeRoomPayload,
    appendLiveBridgeLocalMessage,
    loadLiveBridgeMessages,
    pushLiveBridgePreview,
    // UI handlers
    setLiveBridgeViewerPasswordInput,
    handleLiveBridgeUnlock,
    handleLiveBridgeLock
  };
}
